import { createAsyncThunk } from '@reduxjs/toolkit';
import { axiosInstanceNewAPI } from '@/services/api/utils/axiosInstance';
import { validateDataUsingZodSchema } from '@/utils/validateDataUsingZodSchema';
import { getError } from '@/utils/error-report/getError';
import type { ThunkConfig } from '@/types/store';
import type { License, PageOf } from '@/types/models';
import { licenseSchema } from '@/models/licenseSchema';
import { pageableSchema } from '@/models/pageableSchema';
import { apiPath } from '../apiPath';
import {
  LICENSES_FETCHING_ERROR_PREFIX,
  PROJECT_LICENSE_ASSIGN_ERROR_PREFIX,
  PROJECT_LICENSE_REMOVE_ERROR_PREFIX,
} from './license.constants';

// The License entity has no real versioning - the API always reports this ETag.
const PROJECT_LICENSE_ETAG = '-1';

export const getLicenses = createAsyncThunk<License[], void, ThunkConfig>(
  'license/getLicenses',
  async () => {
    try {
      const response = await axiosInstanceNewAPI.get<PageOf<License>>(apiPath.getLicenses());
      if (validateDataUsingZodSchema(response.data, pageableSchema(licenseSchema))) {
        return response.data.content;
      }
      return [];
    } catch (error) {
      return Promise.reject(getError({ error, prefix: LICENSES_FETCHING_ERROR_PREFIX }));
    }
  },
);

export const assignProjectLicense = createAsyncThunk<
  License,
  { projectId: string; licenseId: number },
  ThunkConfig
>('license/assignProjectLicense', async ({ projectId, licenseId }) => {
  try {
    const response = await axiosInstanceNewAPI.put<License>(
      apiPath.getProjectLicense(projectId),
      { id: licenseId },
      { headers: { 'If-Match': PROJECT_LICENSE_ETAG } },
    );
    return response.data;
  } catch (error) {
    return Promise.reject(getError({ error, prefix: PROJECT_LICENSE_ASSIGN_ERROR_PREFIX }));
  }
});

export const removeProjectLicense = createAsyncThunk<void, string, ThunkConfig>(
  'license/removeProjectLicense',
  async projectId => {
    try {
      await axiosInstanceNewAPI.delete(apiPath.getProjectLicense(projectId), {
        headers: { 'If-Match': PROJECT_LICENSE_ETAG },
      });
      return undefined;
    } catch (error) {
      return Promise.reject(getError({ error, prefix: PROJECT_LICENSE_REMOVE_ERROR_PREFIX }));
    }
  },
);
